/**
 * WEB-2475 — снятие правил «Логики», ссылающихся на блок или вариант ответа,
 * удалённый в редакторе. Зеркало серверного судьи
 * `backend/src/ai-funnels/persistent-id/prune-dangling-rule-refs.ts`
 * (WEB-1975): правило мертво, когда потеряна его область (`scope_ref`), цель
 * перехода или группа условий стала невыполнимой (ALL — хоть одно условие,
 * ANY — все). «Потеряна» — id был ДО удаления и исчез ПОСЛЕ. Оба конца гоняют
 * одну таблицу случаев `prune-dangling-rule-refs.cases.json` (сервер) — что
 * снимет сервер при записи, то кабинет снимает сразу, в том же шаге истории,
 * и показывает в окне подтверждения числом.
 */
import type { Rule } from '@/logic-engine';
import { resolveEditorBlockOptions } from '@/components/editor/resolveEditorBlockOptions';
import type { EditorScreen } from './types/screen';

type Json = Record<string, unknown>;

/** Тот же предел, что у сервера (`MAX_CONDITION_GROUP_DEPTH`). */
const MAX_CONDITION_GROUP_DEPTH = 10;
const ANSWER_SOURCE = 'ANSWER';
const GOTO_ACTION_TYPES: readonly string[] = ['GOTO_SCREEN', 'GOTO_ENDING'];
// Служебная метка для счёта (не текст для человека): правило без id.
const UNNAMED_RULE_ID = '(no id)';

interface AliveIndex {
  ids: Set<string>;
  optionsByBlockId: Map<string, Set<string>>;
}
interface RefJudge {
  next: AliveIndex;
  previous: AliveIndex;
}

export interface PruneDanglingRuleRefsResult<L> {
  /** Правила без висячих ссылок; снимать нечего — тот же массив. */
  logic: L;
  removedRuleIds: string[];
}

function isObject(value: unknown): value is Json {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function collectAliveIds(content: unknown): AliveIndex | null {
  if (!Array.isArray(content) || content.length === 0) return null;
  const ids = new Set<string>();
  const optionsByBlockId = new Map<string, Set<string>>();
  let screensSeen = 0;
  for (const screen of content) {
    if (!isObject(screen)) continue;
    if (nonEmptyString(screen.id)) {
      ids.add(screen.id);
      screensSeen += 1;
    }
    if (!Array.isArray(screen.blocks)) continue;
    for (const block of screen.blocks) {
      if (!isObject(block)) continue;
      const blockId = nonEmptyString(block.id) ? block.id : null;
      if (blockId) ids.add(blockId);
      const optionIds = new Set<string>();
      for (const option of resolveEditorBlockOptions(block)) {
        if (!isObject(option) || !nonEmptyString(option.id)) continue;
        ids.add(option.id);
        optionIds.add(option.id);
      }
      if (blockId && optionIds.size > 0)
        optionsByBlockId.set(blockId, optionIds);
    }
  }
  return screensSeen === 0 ? null : { ids, optionsByBlockId };
}

function isRefLost(ref: string, judge: RefJudge): boolean {
  return !judge.next.ids.has(ref) && judge.previous.ids.has(ref);
}

function isConditionUnsatisfiable(item: Json, judge: RefJudge): boolean {
  if (item.source !== ANSWER_SOURCE) return false;
  const sourceRef = item.source_ref;
  if (!nonEmptyString(sourceRef)) return false;
  if (isRefLost(sourceRef, judge)) return true;
  if (!judge.next.ids.has(sourceRef)) return false;
  const optionIds = judge.next.optionsByBlockId.get(sourceRef);
  if (optionIds === undefined) return false;
  const value = item.value;
  if (nonEmptyString(value))
    return !optionIds.has(value) && isRefLost(value, judge);
  if (Array.isArray(value)) {
    const refs = value.filter(nonEmptyString);
    return (
      refs.length > 0 &&
      refs.every((ref) => !optionIds.has(ref)) &&
      refs.some((ref) => isRefLost(ref, judge))
    );
  }
  return false;
}

function isGroupUnsatisfiable(
  group: unknown,
  judge: RefJudge,
  depth: number,
): boolean {
  if (!isObject(group) || depth >= MAX_CONDITION_GROUP_DEPTH) return false;
  const items = group.items;
  if (!Array.isArray(items) || items.length === 0) return false;
  const verdicts = items.map((item) => {
    if (!isObject(item)) return false;
    if (Array.isArray(item.items))
      return isGroupUnsatisfiable(item, judge, depth + 1);
    return isConditionUnsatisfiable(item, judge);
  });
  if (group.combinator === 'ALL') return verdicts.some(Boolean);
  if (group.combinator === 'ANY') return verdicts.every(Boolean);
  return false;
}

function hasLostActionTarget(rule: Json, judge: RefJudge): boolean {
  const action = rule.action;
  if (!isObject(action) || typeof action.type !== 'string') return false;
  if (!GOTO_ACTION_TYPES.includes(action.type)) return false;
  return nonEmptyString(action.target) && isRefLost(action.target, judge);
}

function isRuleDead(rule: unknown, judge: RefJudge): boolean {
  if (!isObject(rule)) return false;
  if (nonEmptyString(rule.scope_ref) && isRefLost(rule.scope_ref, judge))
    return true;
  if (hasLostActionTarget(rule, judge)) return true;
  return isGroupUnsatisfiable(rule.condition_group, judge, 0);
}

/** Снять правила, чьи ссылки вели на содержимое, удалённое этой правкой. */
export function pruneDanglingRuleRefs<L extends readonly unknown[]>(
  previousContent: unknown,
  nextContent: unknown,
  logic: L,
): PruneDanglingRuleRefsResult<L> {
  // Правил в состоянии может не быть вовсе (старое или подставное состояние
  // редактора) — снимать нечего, удаление идёт как раньше.
  if (!Array.isArray(logic) || logic.length === 0) {
    return { logic, removedRuleIds: [] };
  }
  const next = collectAliveIds(nextContent);
  const previous = collectAliveIds(previousContent);
  if (next === null || previous === null) {
    return { logic, removedRuleIds: [] };
  }
  const judge: RefJudge = { next, previous };
  const removedRuleIds: string[] = [];
  const kept = logic.filter((rule) => {
    if (!isRuleDead(rule, judge)) return true;
    const id = isObject(rule) ? rule.id : undefined;
    removedRuleIds.push(nonEmptyString(id) ? id : UNNAMED_RULE_ID);
    return false;
  });
  if (removedRuleIds.length === 0) return { logic, removedRuleIds };
  return { logic: kept as unknown as L, removedRuleIds };
}

export interface BlockRemoval {
  screenId: string;
  blockId: string;
  /** Есть — удаляется вариант ответа этого блока, нет — блок целиком. */
  optionId?: string;
}

/** Содержимое после удаления блока или варианта (без прочих уборок). */
export function contentWithoutBlockOrOption(
  content: EditorScreen[],
  removal: BlockRemoval,
): EditorScreen[] {
  const { screenId, blockId, optionId } = removal;
  return content.map((s) => {
    if (s.id !== screenId) return s;
    if (optionId === undefined) {
      return { ...s, blocks: s.blocks.filter((b) => b.id !== blockId) };
    }
    return {
      ...s,
      blocks: s.blocks.map((b) =>
        b.id === blockId
          ? {
              ...b,
              options: (b.options ?? []).filter((o) => o.id !== optionId),
            }
          : b,
      ),
    };
  });
}

/** Какие правила снимет удаление — число для окна подтверждения. */
export function rulesLostByRemoval(
  content: EditorScreen[],
  logic: readonly Rule[],
  removal: BlockRemoval,
): string[] {
  return pruneDanglingRuleRefs(
    content,
    contentWithoutBlockOrOption(content, removal),
    logic,
  ).removedRuleIds;
}
